import { getApi } from '@/stores/api'
import { destroySessionToken, getSessionToken } from '@/stores/session'
import { MILLIS } from '@repo/constants'
import { useQuery } from '@tanstack/react-query'

const redirectToLogin = () => window.location.replace(`/login?redirect=${window.location.pathname}`)

export async function handleInvalidSession() {
  destroySessionToken()
  redirectToLogin()
}

export function isAuthError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    (error.status === 401 || error.status === 403)
  )
}

export async function verifySession(signal?: AbortSignal) {
  const sessionToken = getSessionToken()
  if (!sessionToken) {
    redirectToLogin()
    return
  }

  try {
    const api = getApi()
    const res = await api.auth.session.verify.$post(
      { json: { sessionToken } },
      { init: { signal } },
    )
    if (res.status !== 204) {
      handleInvalidSession()
      return res
    }

    return { sessionToken }
  } catch (error) {
    if (isAuthError(error)) {
      handleInvalidSession()
    }
    throw error
  }
}

export function useVerifySession() {
  return useQuery({
    queryKey: ['verifiedSession'],
    queryFn: async ({ signal }) => await verifySession(signal),
    staleTime: MILLIS.SECOND * 25,
    retry: (failureCount, error) => !isAuthError(error) && failureCount < 2,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10_000),
  })
}
